import invariant from 'invariant'
import createClient from 'openapi-fetch'
import {mthen} from '@freckle/maybe'
import {HttpError} from '@freckle/educator-entities/ts/common/exceptions/http-error'
import {PATHS} from '@freckle/educator-entities/ts/common/helpers/paths'
import type {paths as cPaths} from '@freckle/educator-entities/ts/curricula-api/generated-client'
import type {
  paths as fancyPaths,
  operations as fOps
} from '@freckle/educator-entities/ts/fancy-api/generated-client'
import {fancyApiFetch, curriculaApiFetch} from './common/helpers/ajax'

export {HttpError} from '@freckle/educator-entities/ts/common/exceptions/http-error'

export type fancyOperations = fOps
export type curriculaPaths = cPaths

export type CurriculaOk<
  Op extends {get: {responses: {200: {content: {'application/json;charset=utf-8': T}}}}},
  T = Op['get']['responses'][200]['content']['application/json;charset=utf-8']
> = T

export type FancyOk<
  Op extends {responses: {200: {content: {'application/json': T}}}},
  T = Op['responses'][200]['content']['application/json']
> = T

/**
 * `T` without its kebab-case keys, at every depth. The API sends each key in both
 * casings during the migration to camelCase, so code that reads only the
 * camelCase key takes this narrower view.
 */
export type CamelCaseKeysOnly<T> =
  T extends ReadonlyArray<infer U>
    ? Array<CamelCaseKeysOnly<U>>
    : T extends object
      ? {[K in keyof T as K extends `${string}-${string}` ? never : K]: CamelCaseKeysOnly<T[K]>}
      : T

export const curriculaClient = createClient<curriculaPaths>({
  baseUrl: PATHS.textAssetsUrl,
  fetch: curriculaApiFetch
})

const querySerializer = {
  array: {
    style: 'form' as const,
    explode: false
  }
}

export const fancyClient = createClient<fancyPaths>({
  baseUrl: PATHS.unversionedAPIUrl,
  credentials: 'include',
  fetch: fancyApiFetch,
  querySerializer
})

export const fancyClientOmitCredentials = createClient<fancyPaths>({
  baseUrl: PATHS.unversionedAPIUrl,
  credentials: 'omit',
  fetch: fancyApiFetch,
  querySerializer
})

export function formDataSerializer(body: unknown) {
  const formData = new FormData()
  const bodyRecord = body as Record<string, File>
  for (const [name, file] of Object.entries(bodyRecord)) {
    formData.append(name, file, file.name)
  }
  return formData
}

export function extractData<T = unknown>({
  response,
  data
}: {
  data?: T
  response: Response
}): T | Promise<never> {
  // The error handling from our freckleFetch should catch the errors first
  // This is just a redundant check to satisfy type checking
  return response.ok && data !== undefined ? data : Promise.reject(new HttpError(response))
}

export function extractHeaders({response}: {response: Response}): Headers | Promise<never> {
  // The error handling from our freckleFetch should catch the errors first
  // This is just a redundant check to satisfy type checking
  return response.ok ? response.headers : Promise.reject(new HttpError(response))
}

export function extractDataAndHeaders<T = unknown>({
  response,
  data
}: {
  data?: T
  response: Response
}): {data: T; headers: Headers} | Promise<never> {
  // The error handling from our freckleFetch should catch the errors first
  // This is just a redundant check to satisfy type checking
  return response.ok && data !== undefined
    ? {data, headers: response.headers}
    : Promise.reject(new HttpError(response))
}

export function ensureOk<Response extends {ok: boolean}>({
  response
}: {
  response: Response
}): Promise<void> {
  if (!response.ok) return Promise.reject(new HttpError(response))

  return Promise.resolve()
}

export async function downloadFile(
  blob: Blob | undefined,
  headers: Headers,
  defaultFilename: string
): Promise<void> {
  invariant(blob !== undefined, 'No blob provided')

  const disposition = headers.get('Content-Disposition')

  const reader = new FileReader()
  reader.onload = e => {
    const anchor = document.createElement('a')
    anchor.style.display = 'none'

    const target = e.target
    if (target instanceof FileReader && typeof target.result === 'string') {
      const finalFilename = contentDispositionFilename(disposition) ?? defaultFilename
      anchor.href = target.result
      anchor.download = finalFilename
      anchor.click()
    }
  }

  reader.onerror = error => {
    console.error('FileReader error', {error})
  }

  reader.readAsDataURL(blob)
}

function contentDispositionFilename(mDisposition?: string | null): string | undefined | null {
  return mthen(mDisposition, disposition =>
    mthen(
      disposition.trim().match(/attachment; filename="(.*)"/),
      ([_ignore, filename]) => filename
    )
  )
}
